import { FC, useState, useEffect, CSSProperties } from 'react';
import { faChain } from '@fortawesome/pro-regular-svg-icons';
import { BoxV2 as Box, Flex, Grid } from 'portal-commons';

import RcsSectionBlock from './RcsSectionBlock';
import { campaignController, CampaignDetail } from '@/apis/core';
import StatusIndicator from './StatusIndicator';
import { humanize } from '@/utils/string';
import { remapTCRStatus } from '../../utils';

interface CarrierStatusSectionProps {
  campaign: CampaignDetail;
}

const CarrierStatusSection: FC<CarrierStatusSectionProps> = ({ campaign }) => {
  const [verificationStatus, setVerificationStatus] = useState<string | null>();

  useEffect(() => {
    campaignController
      .getVerification(campaign.uid, { silent: true })
      .then((res) => {
        setVerificationStatus(res?.verificationStatus);
      });
  }, [campaign]);

  return (
    <RcsSectionBlock
      icon={faChain}
      title="Carrier Status"
      showDivider={false}
      data-testid="campaignCarrierStatus"
    >
      <Grid
        sx={{
          display: 'grid',
          width: '100%',
          gridTemplateColumns: 'minmax(110px, 11%) repeat(4, 22.1%)',
          fontWeight: 'semiBold',
          fontSize: 'H200',
          minHeight: '80px',
          gap: 0,
        }}
      >
        <Flex
          sx={{
            backgroundColor: 't.blue120',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#fff',
          }}
          data-testid="carrierItemMnoId"
        >
          {campaign?.rulesEngineResultsJson
            ? JSON.parse(campaign.rulesEngineResultsJson).mno
            : '--'}
        </Flex>
        <Attribute
          title="CAMPAIGN VERIFICATION"
          style={{ marginRight: '4px' }}
          testId="carrierItemVerificationStatus"
        >
          {verificationStatus && (
            <>
              <StatusIndicator
                variant={
                  verificationStatus === 'VERIFIED' ? 'success' : 'error'
                }
              />
              {humanize(verificationStatus)}
            </>
          )}
        </Attribute>
        <Attribute title="CAMPAIGN STATUS" testId="carrierItemCampaignStatus">
          {campaign.operationStatus && (
            <>
              <StatusIndicator
                variant={
                  campaign.operationStatus === 'ACTIVE' ? 'success' : 'error'
                }
              />
              {humanize(campaign.operationStatus)}
            </>
          )}
        </Attribute>
        <Attribute title="DCA NAME" testId="carrierItemDcaName">
          {campaign.dcaName}
        </Attribute>
        <Attribute title="TCR STATUS" testId="carrierItemTcrStatus">
          {campaign.status && (
            <>
              <StatusIndicator
                variant={campaign.status === 'ACTIVE' ? 'success' : 'error'}
              />
              {humanize(remapTCRStatus(campaign.status, undefined))}
            </>
          )}
        </Attribute>
      </Grid>
    </RcsSectionBlock>
  );
};

const Attribute: FC<{
  title: string;
  style?: CSSProperties;
  testId: string;
}> = ({ title, children, testId, style }) => {
  return (
    <Flex
      sx={{ flexDirection: 'column', textAlign: 'center', flex: 1, ...style }}
      data-testid={testId}
    >
      <Box
        sx={{
          color: 't.blue120',
          py: '8px',
          px: '4px',
          flex: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#E5EFF2',
        }}
      >
        {title}
      </Box>
      <Box
        sx={{
          color: '#666E71',
          backgroundColor: 't.black05',
          flex: 1,
          py: '8px',
          px: '4px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children || 'N/A'}
      </Box>
    </Flex>
  );
};

export default CarrierStatusSection;
